import React from "react";
import { FiFolder, FiRefreshCw } from "react-icons/fi";
import { PiBrain } from "react-icons/pi";
import { useMatch } from "react-router";

import MemoryView from "components/MemoryView";

import useScopeFilesQuery from "services/fileApi/hooks/useScopeFilesQuery";
import useThreadsQuery from "services/dbApi/hooks/useThreadsQuery";
import FileList from "./components/FileList";
import FileListSkeleton from "./components/FileListSkeleton";
import useDeleteThreadFiles from "./hooks/useDeleteThreadFiles";
import styles from "./FileExplorer.module.scss";

type PaneView = "files" | "memory";

const PANE_VIEW_KEY = "rightPaneView";

const readPaneView = (): PaneView =>
  localStorage.getItem(PANE_VIEW_KEY) === "memory" ? "memory" : "files";

/**
 * The collapsible pane on the right of the chat layout that lists the active
 * thread's files (ADR 071), or shows what the assistant remembers (UI ADR 078).
 * `ChatLayout` keeps it mounted across navigation.
 */
function FileExplorer() {
  // Undefined on the new-chat route, which has no thread yet.
  const chatId = useMatch("/chats/:chatId")?.params.chatId;

  // The pane starts collapsed until the user expands it.
  const [isCollapsed, setIsCollapsed] = React.useState(
    () => localStorage.getItem("fileExplorerCollapsed") !== "false"
  );

  // Persist collapsed state to localStorage whenever it changes
  React.useEffect(() => {
    localStorage.setItem("fileExplorerCollapsed", String(isCollapsed));
  }, [isCollapsed]);

  const [view, setView] = React.useState<PaneView>(readPaneView);

  React.useEffect(() => {
    localStorage.setItem(PANE_VIEW_KEY, view);
  }, [view]);

  // Opens the pane on `target`, switches to it, or collapses the pane when
  // `target` is already showing.
  const selectView = (target: PaneView) => {
    if (!isCollapsed && view === target) {
      setIsCollapsed(true);
      return;
    }
    setView(target);
    setIsCollapsed(false);
  };

  const filesQuery = useScopeFilesQuery({ threadId: chatId });
  const threadsQuery = useThreadsQuery();
  const files = filesQuery.data ?? [];
  // Undefined while the threads load, or when no thread has this ID.
  const chatThread = threadsQuery.data?.find((thread) => thread.id === chatId);
  // Called here rather than in FileList, because deleting the last file
  // unmounts FileList. A failed messages PUT only shows its error screen while
  // the hook's caller is still mounted.
  const { deleteFiles, canDelete } = useDeleteThreadFiles({ chatThread });

  // errorUpdatedAt stays set while a retry is in flight, so the error UI does
  // not flash back to the skeleton (ADR 066).
  const filesFailed =
    filesQuery.errorUpdatedAt > 0 && filesQuery.data === undefined;
  // The file actions need the thread. A thread missing from a successful fetch
  // shows the empty message instead.
  const threadsFailed =
    threadsQuery.errorUpdatedAt > 0 && threadsQuery.data === undefined;
  const isRetrying = filesQuery.isFetching || threadsQuery.isFetching;
  const hasFiles = files.length > 0 && chatThread !== undefined;
  const fileCountLabel =
    files.length === 1 ? "1 file" : `${files.length} files`;

  const retry = () => {
    if (filesFailed) void filesQuery.refetch();
    if (threadsFailed) void threadsQuery.refetch();
  };

  let content: React.ReactNode;
  if (view === "memory") {
    // A thread ID that matches no thread shows only the user's memories.
    content = <MemoryView threadId={chatThread?.id} />;
  } else if (chatId === undefined) {
    content = <p className={styles.statusMessage}>No files in this chat</p>;
  } else if (filesFailed || threadsFailed) {
    content = (
      <div className={styles.loadError}>
        <p className={styles.errorMessage}>Couldn&apos;t load files</p>
        <button
          type="button"
          onClick={retry}
          className={styles.retryButton}
          disabled={isRetrying}
        >
          <FiRefreshCw
            aria-hidden="true"
            className={isRetrying ? styles.retryIconSpinning : undefined}
          />
          <span>{isRetrying ? "Loading…" : "Retry"}</span>
        </button>
      </div>
    );
  } else if (filesQuery.isLoading || threadsQuery.isLoading) {
    content = <FileListSkeleton />;
  } else if (files.length === 0 || chatThread === undefined) {
    // No files, or a thread ID that matches no thread.
    content = <p className={styles.statusMessage}>No files in this chat</p>;
  } else {
    // Keyed so that switching threads resets the selection and the search.
    content = (
      <FileList
        key={chatId}
        chatThread={chatThread}
        files={files}
        deleteFiles={deleteFiles}
        canDelete={canDelete}
      />
    );
  }

  let title: string;
  if (view === "memory") {
    title = "Memory";
  } else if (hasFiles) {
    title = fileCountLabel;
  } else {
    title = "Files";
  }

  return (
    <aside
      className={`${styles.fileExplorer} ${
        isCollapsed ? styles.collapsed : ""
      }`}
      aria-label={view === "memory" ? "Memory" : "File explorer"}
    >
      <div className={styles.header}>
        {!isCollapsed && <h3 className={styles.title}>{title}</h3>}
        <div className={styles.toggleGroup}>
          <button
            type="button"
            onClick={() => selectView("files")}
            className={`${styles.toggleButton} ${
              !isCollapsed && view === "files" ? styles.toggleButtonActive : ""
            }`}
            aria-label={filesButtonLabel(isCollapsed, view)}
            aria-expanded={!isCollapsed && view === "files"}
          >
            <FiFolder />
          </button>
          {isCollapsed && hasFiles && (
            <span className={styles.countBadge} title={fileCountLabel}>
              {files.length}
            </span>
          )}
          <button
            type="button"
            onClick={() => selectView("memory")}
            className={`${styles.toggleButton} ${styles.memoryButton} ${
              !isCollapsed && view === "memory" ? styles.toggleButtonActive : ""
            }`}
            aria-label={memoryButtonLabel(isCollapsed, view)}
            aria-expanded={!isCollapsed && view === "memory"}
          >
            <PiBrain />
          </button>
        </div>
      </div>
      {/* Collapsing unmounts the list, which clears the selection and the
          search. */}
      {!isCollapsed && <div className={styles.body}>{content}</div>}
    </aside>
  );
}

function filesButtonLabel(isCollapsed: boolean, view: PaneView) {
  if (isCollapsed) return "Expand file explorer";
  return view === "files" ? "Collapse file explorer" : "Show files";
}

function memoryButtonLabel(isCollapsed: boolean, view: PaneView) {
  if (isCollapsed) return "Open memory";
  return view === "memory" ? "Close memory" : "Show memory";
}

export default React.memo(FileExplorer);
